import {
  ContextMenu,
  ContextMenuOption,
  Icon,
  useScrollIndicators,
  useWheelInline,
} from '@infotech-soe/erp-ui-kit';
import {
  CategoryName,
  DisplayModeName,
  IInParamsValues,
} from '@infotech-soe/metaschema';
import classNames from 'classnames';
import React, {
  DragEvent,
  memo,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';

import { isDroppable } from '../../../../helpers';
import { IPanelLink } from '../../../../models/dataset/DatasetRelated';
import { useContextMenu } from '../../../hooks/useContextMenu';
import { ActionNames } from '../../menu/ApplicationMenu';
import { itemMessages } from '../../menu/messages';

import PanelLink from './PanelLink';
import useStyles, {
  DRAGGABLE_CELL_CLASS,
  DRAGGABLE_CELL_ID,
  DRAGOVER_CLASS,
} from './styles';

export const DRAGGABLE_EVENT_NAME = 'panel-draggable-event';

const isSamePanelLink = (a: IPanelLink, b: IPanelLink): boolean =>
  a.category === b.category &&
  a.displayMode === b.displayMode &&
  a.action === b.action;

export interface PanelLinksProps {
  isAlreadyOpen: (
    categoryName: CategoryName,
    displayModeName: DisplayModeName
  ) => boolean;
  panelLinks: IPanelLink[];
  onActivateDisplayMode: (
    categoryName: CategoryName,
    displayModeName: DisplayModeName
  ) => void;
  onCloseMenu: () => void;
  onGetPanelLinks: () => void;
  onOpenDisplayMode: (
    categoryName: CategoryName,
    displayModeName: DisplayModeName,
    inParams?: IInParamsValues,
    action?: string,
    withDefaultConfig?: boolean
  ) => void;
  onSavePanelLinks: (links: IPanelLink[]) => void;
}

const PanelLinks = ({
  isAlreadyOpen,
  panelLinks,
  onActivateDisplayMode,
  onCloseMenu,
  onGetPanelLinks,
  onOpenDisplayMode,
  onSavePanelLinks,
}: PanelLinksProps): React.JSX.Element => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const contextMenu = useContextMenu();

  const panelRef = useRef<HTMLDivElement>(null);
  const [contextMenuLink, setContextMenuLink] = useState<IPanelLink | null>(
    null
  );

  useWheelInline(panelRef);

  const isUnique = useCallback(
    (newLink: IPanelLink) =>
      !panelLinks.some(link => isSamePanelLink(link, newLink)),
    [panelLinks]
  );

  const handleSortDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      event.stopPropagation();
      const { dataTransfer, currentTarget } = event;

      if (!isDroppable(event, DRAGGABLE_EVENT_NAME)) {
        return;
      }

      const links = [...panelLinks];
      const menuItemProps = dataTransfer.getData(
        `${DRAGGABLE_EVENT_NAME}/menuItemProps`
      );
      const overedIndex = Number(
        (document.querySelector(`.${DRAGOVER_CLASS}`) as HTMLElement)?.dataset
          .index
      );

      if (menuItemProps) {
        const menuItem: IPanelLink = JSON.parse(menuItemProps);

        if (isUnique(menuItem)) {
          links.splice(overedIndex, 0, menuItem);
        }
      } else {
        const prevIndex = Number(
          dataTransfer.getData(`${DRAGGABLE_EVENT_NAME}/prevIndex`)
        );
        const prevItem = links[prevIndex];

        links.splice(Number(prevIndex), 1);
        links.splice(overedIndex, 0, prevItem);
      }

      onSavePanelLinks([...links]);
      currentTarget.classList.remove(DRAGGABLE_CELL_CLASS, DRAGOVER_CLASS);
    },
    [panelLinks, onSavePanelLinks, isUnique]
  );

  const handleCellDrop = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      const memuItemProps = event.dataTransfer.getData(
        `${DRAGGABLE_EVENT_NAME}/menuItemProps`
      );

      if (memuItemProps) {
        const menuItem: IPanelLink = JSON.parse(memuItemProps);
        if (isUnique(menuItem) && isDroppable(event, DRAGGABLE_EVENT_NAME)) {
          onSavePanelLinks([...panelLinks, menuItem]);
        }
      }
    },
    [isUnique, onSavePanelLinks, panelLinks]
  );

  useEffect(() => {
    onGetPanelLinks();
  }, [onGetPanelLinks]);

  const handleDragStart = useCallback(
    (index: number) => (event: DragEvent<HTMLDivElement>) => {
      event.dataTransfer.setData(
        `${DRAGGABLE_EVENT_NAME}/prevIndex`,
        index.toString()
      );
    },
    []
  );

  const hanleRemoveLink = useCallback(
    (linkToRemove: IPanelLink) => (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      onSavePanelLinks(
        panelLinks.filter(link => !isSamePanelLink(link, linkToRemove))
      );
    },
    [onSavePanelLinks, panelLinks]
  );

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (isDroppable(event, DRAGGABLE_EVENT_NAME)) {
      event.currentTarget.classList.add(DRAGOVER_CLASS);
    }
  }, []);

  const handleDragLeave = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.currentTarget.classList.remove(DRAGOVER_CLASS);
  }, []);

  const handleClickLink = useCallback(
    (link: IPanelLink) =>
      (event: MouseEvent<HTMLDivElement> | KeyboardEvent) => {
        const { category, displayMode, action, inParams } = link;

        const activateOpen =
          isAlreadyOpen(category, displayMode) && !event?.ctrlKey;

        if (activateOpen) {
          onActivateDisplayMode(category, displayMode);
        } else {
          onOpenDisplayMode(category, displayMode, inParams, action);
        }
        onCloseMenu();
      },
    [isAlreadyOpen, onActivateDisplayMode, onCloseMenu, onOpenDisplayMode]
  );

  const handleContextMenu = useCallback(
    (link: IPanelLink) => (e: MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setContextMenuLink(link);
      contextMenu.setPositionX(e.clientX);
      contextMenu.setPositionY(e.clientY);
      contextMenu.open();
    },
    [contextMenu]
  );

  const contextMenuOptions = useMemo<Array<ContextMenuOption<ActionNames>>>(
    () => [
      {
        type: 'listitem',
        value: ActionNames.OPEN,
        label: formatMessage(itemMessages[ActionNames.OPEN]),
        icon: 'create',
      },
      {
        type: 'listitem',
        value: ActionNames.ACTIVATE,
        label: formatMessage(itemMessages[ActionNames.ACTIVATE]),
        icon: 'ui-tab',
        disabled:
          !contextMenuLink ||
          !isAlreadyOpen(contextMenuLink.category, contextMenuLink.displayMode),
      },
      {
        type: 'listitem',
        value: ActionNames.OPEN_DEFAULT,
        label: formatMessage(itemMessages[ActionNames.OPEN_DEFAULT]),
        icon: 'reforming',
      },
    ],
    [contextMenuLink, formatMessage, isAlreadyOpen]
  );

  const handleContextMenuClick = useCallback(
    (actionName: ActionNames) => {
      if (contextMenuLink) {
        const { category, displayMode, inParams, action } = contextMenuLink;
        if (actionName === ActionNames.OPEN)
          onOpenDisplayMode(category, displayMode, inParams, action);
        else if (actionName === ActionNames.ACTIVATE)
          onActivateDisplayMode(category, displayMode);
        else if (actionName === ActionNames.OPEN_DEFAULT)
          onOpenDisplayMode(category, displayMode, inParams, action, true);
        onCloseMenu();
      }
      setContextMenuLink(null);
      contextMenu.close();
    },
    [
      contextMenu,
      contextMenuLink,
      onActivateDisplayMode,
      onCloseMenu,
      onOpenDisplayMode,
    ]
  );

  const handleCloseContextMenu = useCallback(() => {
    setContextMenuLink(null);
    contextMenu.close();
  }, [contextMenu]);

  const SCROLL_STEP = 120;
  const handleScrollLeft = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    if (e.ctrlKey) {
      panelRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      panelRef.current?.scrollBy({ left: -SCROLL_STEP, behavior: 'smooth' });
    }
  }, []);
  const handleScrollRight = useCallback((e: MouseEvent<HTMLButtonElement>) => {
    if (e.ctrlKey) {
      panelRef.current?.scrollTo({
        left: panelRef.current.scrollWidth,
        behavior: 'smooth',
      });
    } else {
      panelRef.current?.scrollBy({ left: SCROLL_STEP, behavior: 'smooth' });
    }
  }, []);

  const { indicators } = useScrollIndicators(panelRef, 'horizontal');

  return (
    <div className={classes.scrollContainer}>
      <div
        className={classes.links}
        ref={panelRef}
        id={DRAGGABLE_CELL_ID}
        onDrop={handleCellDrop}
        onDragOver={event => event.preventDefault()}
      >
        {panelLinks.map((link, index) => (
          <PanelLink
            // eslint-disable-next-line react/no-array-index-key
            key={link.label + index}
            data-index={index}
            icon={link.icon}
            label={link.label}
            onClick={handleClickLink(link)}
            onContextMenu={handleContextMenu(link)}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart(index)}
            onDrop={handleSortDrop}
            onRemoveItem={hanleRemoveLink(link)}
          />
        ))}
      </div>
      {(Object.keys(indicators) as Array<keyof typeof indicators>).map(side => (
        <button
          key={side}
          type="button"
          className={classNames(
            classes.scrollIndicator,
            indicators[side] && classes.show
          )}
          onClick={side === 'left' ? handleScrollLeft : handleScrollRight}
        >
          <Icon
            name={side === 'left' ? 'menu-arrow-back' : 'menu-arrow-forward'}
          />
        </button>
      ))}
      <ContextMenu
        open={contextMenu.isOpen}
        options={contextMenuOptions}
        x={contextMenu.positionX}
        y={contextMenu.positionY}
        onClick={handleContextMenuClick}
        onOpen={handleCloseContextMenu}
      />
    </div>
  );
};

export default memo(PanelLinks);
